import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/ui/use-toast';
import { Receipt, User, FileText, Download, Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { formatLocalDate } from '@/lib/utils';

interface TaxProfile {
  id: number;
  profileName: string;
  rfc: string;
  legalName: string;
  taxRegimeCode: string;
  zipCode: string;
  fiscalAddress?: string;
  emailForInvoices: string;
  defaultUsoCfdi: string;
  isDefault: boolean;
}

interface InvoiceablePayment {
  id: number;
  date: string;
  amount: string;
  paymentMethod: string;
  reference: string;
  invoiceNumber: string;
  invoiceTotal: string;
  invoiceId: number;
}

interface CfdiInvoice {
  id: number;
  uuid: string;
  folioFiscal: string;
  receptorRfc: string;
  receptorName: string;
  total: string;
  status: string;
  emittedAt: string;
  hasXml: boolean;
  hasPdf: boolean;
  taxProfileName?: string;
}

interface TaxRegime {
  code: string;
  name: string;
}

interface UsoCfdi {
  code: string;
  name: string;
}

const paymentMethodLabels: Record<string, string> = {
  TRANSFER: 'Transferencia',
  CHECK: 'Cheque',
  CASH: 'Efectivo',
  CARD: 'Tarjeta',
  OTHER: 'Otro',
};

const statusLabels: Record<string, { label: string; color: string }> = {
  stamped: { label: 'Timbrado', color: 'bg-green-100 text-green-800' },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-800' },
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800' },
};

export default function StudentBilling() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('profiles');
  const [loading, setLoading] = useState(true);

  const [taxProfiles, setTaxProfiles] = useState<TaxProfile[]>([]);
  const [taxRegimes, setTaxRegimes] = useState<TaxRegime[]>([]);
  const [usosCfdi, setUsosCfdi] = useState<UsoCfdi[]>([]);
  const [payments, setPayments] = useState<InvoiceablePayment[]>([]);
  const [policy, setPolicy] = useState<{ maxDays: number; cutoffDate: string } | null>(null);
  const [invoices, setInvoices] = useState<CfdiInvoice[]>([]);

  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [editingProfile, setEditingProfile] = useState<TaxProfile | null>(null);
  const [profileForm, setProfileForm] = useState({
    profileName: '',
    rfc: '',
    legalName: '',
    taxRegimeCode: '',
    zipCode: '',
    fiscalAddress: '',
    emailForInvoices: '',
    defaultUsoCfdi: 'D10',
    isDefault: false,
  });

  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [selectedPayments, setSelectedPayments] = useState<number[]>([]);
  const [invoiceForm, setInvoiceForm] = useState({
    taxProfileId: '',
    usoCfdi: 'D10',
  });
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTaxRegimes();
    fetchUsosCfdi();
  }, []);

  useEffect(() => {
    if (activeTab === 'profiles') {
      fetchTaxProfiles();
    } else if (activeTab === 'self-invoice') {
      fetchTaxProfiles();
      fetchInvoiceablePayments();
    } else if (activeTab === 'history') {
      fetchInvoices();
    }
  }, [activeTab]);

  const fetchTaxProfiles = async () => {
    try {
      const response = await fetch('/api/student-billing/tax-profiles', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTaxProfiles(data);
      }
    } catch (error) {
      console.error('Error fetching tax profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTaxRegimes = async () => {
    try {
      const response = await fetch('/api/student-billing/tax-regimes', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTaxRegimes(data);
      }
    } catch (error) {
      console.error('Error fetching tax regimes:', error);
    }
  };

  const fetchUsosCfdi = async () => {
    try {
      const response = await fetch('/api/student-billing/uso-cfdi-options', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUsosCfdi(data);
      }
    } catch (error) {
      console.error('Error fetching usos CFDI:', error);
    }
  };

  const fetchInvoiceablePayments = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/student-billing/invoiceable-payments', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPayments(data.payments || []);
        setPolicy(data.policy || null);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/student-billing/my-invoices', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setInvoices(data);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateRfc = (rfc: string): boolean => {
    const rfcPattern = /^[A-ZÑ&]{3,4}\d{6}[A-Z\d]{3}$/i;
    return rfcPattern.test(rfc);
  };

  const handleSaveProfile = async () => {
    if (!validateRfc(profileForm.rfc)) {
      toast({ title: 'Error', description: 'El RFC tiene un formato inválido', variant: 'destructive' });
      return;
    }

    if (!profileForm.profileName || !profileForm.legalName || !profileForm.taxRegimeCode || !profileForm.zipCode || !profileForm.emailForInvoices) {
      toast({ title: 'Error', description: 'Complete todos los campos obligatorios', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const url = editingProfile 
        ? `/api/student-billing/tax-profiles/${editingProfile.id}`
        : '/api/student-billing/tax-profiles';
      
      const response = await fetch(url, {
        method: editingProfile ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(profileForm),
      });

      if (response.ok) {
        toast({ title: 'Éxito', description: editingProfile ? 'Perfil actualizado' : 'Perfil creado' });
        setShowProfileDialog(false);
        resetProfileForm();
        fetchTaxProfiles();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.error || 'Error al guardar', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Error de conexión', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProfile = async (id: number) => {
    if (!confirm('¿Está seguro de eliminar este perfil fiscal?')) return;

    try {
      const response = await fetch(`/api/student-billing/tax-profiles/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast({ title: 'Éxito', description: 'Perfil eliminado' });
        fetchTaxProfiles();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.error, variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Error de conexión', variant: 'destructive' });
    }
  };

  const openEditDialog = (profile: TaxProfile) => {
    setEditingProfile(profile);
    setProfileForm({
      profileName: profile.profileName,
      rfc: profile.rfc,
      legalName: profile.legalName,
      taxRegimeCode: profile.taxRegimeCode,
      zipCode: profile.zipCode,
      fiscalAddress: profile.fiscalAddress || '',
      emailForInvoices: profile.emailForInvoices,
      defaultUsoCfdi: profile.defaultUsoCfdi || 'D10',
      isDefault: profile.isDefault,
    });
    setShowProfileDialog(true);
  };

  const resetProfileForm = () => {
    setEditingProfile(null);
    setProfileForm({
      profileName: '',
      rfc: '',
      legalName: '',
      taxRegimeCode: '',
      zipCode: '',
      fiscalAddress: '',
      emailForInvoices: '',
      defaultUsoCfdi: 'D10',
      isDefault: false,
    });
  };

  const togglePaymentSelection = (paymentId: number) => {
    setSelectedPayments(prev =>
      prev.includes(paymentId)
        ? prev.filter(id => id !== paymentId)
        : [...prev, paymentId]
    );
  };

  const openInvoiceDialog = () => {
    if (selectedPayments.length === 0) {
      toast({ title: 'Atención', description: 'Seleccione al menos un pago', variant: 'destructive' });
      return;
    }

    if (taxProfiles.length === 0) {
      toast({ title: 'Atención', description: 'Primero agregue un perfil fiscal', variant: 'destructive' });
      return;
    }

    const defaultProfile = taxProfiles.find(p => p.isDefault) || taxProfiles[0];
    setInvoiceForm({
      taxProfileId: defaultProfile.id.toString(),
      usoCfdi: defaultProfile.defaultUsoCfdi || 'D10',
    });
    setShowInvoiceDialog(true);
  };

  const handleGenerateInvoice = async () => {
    if (!invoiceForm.taxProfileId || !invoiceForm.usoCfdi) {
      toast({ title: 'Error', description: 'Complete todos los campos', variant: 'destructive' });
      return;
    }

    setGeneratingInvoice(true);
    try {
      const response = await fetch('/api/student-billing/generate-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          paymentIds: selectedPayments,
          taxProfileId: parseInt(invoiceForm.taxProfileId),
          usoCfdi: invoiceForm.usoCfdi,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        toast({ title: 'Factura generada', description: `UUID: ${result.uuid}` });
        setShowInvoiceDialog(false);
        setSelectedPayments([]);
        fetchInvoiceablePayments();
        setActiveTab('history');
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.error || 'Error al generar factura', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Error de conexión', variant: 'destructive' });
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const handleDownload = async (invoiceId: number, type: 'xml' | 'pdf') => {
    try {
      const response = await fetch(`/api/student-billing/invoices/${invoiceId}/download/${type}`, {
        credentials: 'include',
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `factura.${type}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        toast({ title: 'Error', description: `No se pudo descargar el ${type.toUpperCase()}`, variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Error de conexión', variant: 'destructive' });
    }
  };

  const formatCurrency = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(num);
  };

  // Date-only values (payment.date, policy.cutoffDate) — never carry a
  // meaningful time component, so parse the calendar digits literally.
  const formatDate = (dateStr: string) => {
    return formatLocalDate(dateStr, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Real instants (invoice.emittedAt) — convert to Mexico City wall-clock
  // time instead of truncating the UTC date, since the UTC and Mexico
  // calendar dates can legitimately differ near midnight.
  const formatEmittedDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'America/Mexico_City',
    });
  };

  const selectedTotal = payments
    .filter(p => selectedPayments.includes(p.id))
    .reduce((sum, p) => sum + parseFloat(p.amount), 0);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Receipt className="w-8 h-8 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mis Facturas</h1>
          <p className="text-gray-600">Gestiona tus datos fiscales y genera tus facturas</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="profiles" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            Mis Datos Fiscales
          </TabsTrigger>
          <TabsTrigger value="self-invoice" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Autofacturación
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <Receipt className="w-4 h-4" />
            Historial
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profiles" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Perfiles Fiscales</CardTitle>
                <CardDescription>Administra los RFC a los que puedes facturar</CardDescription>
              </div>
              <Button onClick={() => { resetProfileForm(); setShowProfileDialog(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Agregar RFC
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Cargando...</div>
              ) : taxProfiles.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No tienes perfiles fiscales. Agrega uno para poder facturar.
                </div>
              ) : (
                <div className="space-y-4">
                  {taxProfiles.map(profile => (
                    <div key={profile.id} className="border rounded-lg p-4 flex justify-between items-start">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{profile.profileName}</span>
                          {profile.isDefault && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Predeterminado</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">RFC: {profile.rfc}</p>
                        <p className="text-sm text-gray-600">{profile.legalName}</p>
                        <p className="text-sm text-gray-500">Email: {profile.emailForInvoices}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEditDialog(profile)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDeleteProfile(profile.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="self-invoice" className="mt-6 space-y-4">
          {policy && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                Puedes facturar pagos realizados en los últimos <strong>{policy.maxDays} días</strong>.
                Los pagos anteriores al {formatDate(policy.cutoffDate)} ya no son facturables.
              </p>
            </div>
          )}

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Pagos Disponibles para Facturar</CardTitle>
                <CardDescription>Selecciona los pagos que deseas facturar</CardDescription>
              </div>
              {selectedPayments.length > 0 && (
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600">
                    {selectedPayments.length} pago(s) - Total: {formatCurrency(selectedTotal)}
                  </span>
                  <Button onClick={openInvoiceDialog}>
                    <FileText className="w-4 h-4 mr-2" />
                    Solicitar Factura
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Cargando...</div>
              ) : payments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No tienes pagos disponibles para facturar en este momento.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2"></th>
                        <th className="text-left py-3 px-2">Fecha</th>
                        <th className="text-left py-3 px-2">Concepto</th>
                        <th className="text-left py-3 px-2">Método</th>
                        <th className="text-right py-3 px-2">Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map(payment => (
                        <tr key={payment.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-2">
                            <Checkbox
                              checked={selectedPayments.includes(payment.id)}
                              onCheckedChange={() => togglePaymentSelection(payment.id)}
                            />
                          </td>
                          <td className="py-3 px-2">{formatDate(payment.date)}</td>
                          <td className="py-3 px-2">
                            <span className="font-medium">{payment.invoiceNumber}</span>
                            {payment.reference && <span className="text-gray-500 ml-2">({payment.reference})</span>}
                          </td>
                          <td className="py-3 px-2">{paymentMethodLabels[payment.paymentMethod] || payment.paymentMethod}</td>
                          <td className="py-3 px-2 text-right font-medium">{formatCurrency(payment.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Historial de Facturas</CardTitle>
              <CardDescription>Consulta y descarga tus facturas emitidas</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Cargando...</div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No tienes facturas generadas aún.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">Folio</th>
                        <th className="text-left py-3 px-2">UUID</th>
                        <th className="text-left py-3 px-2">Receptor</th>
                        <th className="text-left py-3 px-2">Fecha</th>
                        <th className="text-right py-3 px-2">Total</th>
                        <th className="text-center py-3 px-2">Estado</th>
                        <th className="text-center py-3 px-2">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map(invoice => (
                        <tr key={invoice.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-2 font-medium">{invoice.folioFiscal}</td>
                          <td className="py-3 px-2 text-xs text-gray-500 font-mono">{invoice.uuid?.slice(0, 8)}...</td>
                          <td className="py-3 px-2">
                            <div>{invoice.receptorName}</div>
                            <div className="text-xs text-gray-500">{invoice.receptorRfc}</div>
                          </td>
                          <td className="py-3 px-2">{invoice.emittedAt ? formatEmittedDate(invoice.emittedAt) : '-'}</td>
                          <td className="py-3 px-2 text-right font-medium">{formatCurrency(invoice.total)}</td>
                          <td className="py-3 px-2 text-center">
                            <span className={`text-xs px-2 py-1 rounded ${statusLabels[invoice.status]?.color || 'bg-gray-100'}`}>
                              {statusLabels[invoice.status]?.label || invoice.status}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <div className="flex justify-center gap-1">
                              {invoice.hasXml && (
                                <Button variant="ghost" size="sm" onClick={() => handleDownload(invoice.id, 'xml')}>
                                  <Download className="w-4 h-4" />
                                  <span className="ml-1 text-xs">XML</span>
                                </Button>
                              )}
                              {invoice.hasPdf && (
                                <Button variant="ghost" size="sm" onClick={() => handleDownload(invoice.id, 'pdf')}>
                                  <Download className="w-4 h-4" />
                                  <span className="ml-1 text-xs">PDF</span>
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingProfile ? 'Editar Perfil Fiscal' : 'Nuevo Perfil Fiscal'}</DialogTitle>
            <DialogDescription>
              Ingresa los datos fiscales del RFC al que deseas facturar
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="profileName">Nombre del Perfil *</Label>
              <Input
                id="profileName"
                placeholder="Ej: Papá, Personal, Empresa"
                value={profileForm.profileName}
                onChange={(e) => setProfileForm({ ...profileForm, profileName: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="rfc">RFC *</Label>
              <Input
                id="rfc"
                placeholder="XAXX010101000"
                value={profileForm.rfc}
                onChange={(e) => setProfileForm({ ...profileForm, rfc: e.target.value.toUpperCase() })}
                maxLength={13}
              />
              {profileForm.rfc && !validateRfc(profileForm.rfc) && (
                <p className="text-sm text-red-500 mt-1">RFC con formato inválido</p>
              )}
            </div>

            <div>
              <Label htmlFor="legalName">Razón Social / Nombre Completo *</Label>
              <Input
                id="legalName"
                placeholder="Nombre como aparece en la constancia fiscal"
                value={profileForm.legalName}
                onChange={(e) => setProfileForm({ ...profileForm, legalName: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="taxRegimeCode">Régimen Fiscal *</Label>
              <Select
                value={profileForm.taxRegimeCode}
                onValueChange={(value) => setProfileForm({ ...profileForm, taxRegimeCode: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione régimen fiscal" />
                </SelectTrigger>
                <SelectContent>
                  {taxRegimes.map(regime => (
                    <SelectItem key={regime.code} value={regime.code}>
                      {regime.code} - {regime.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="zipCode">Código Postal *</Label>
              <Input
                id="zipCode"
                placeholder="12345"
                value={profileForm.zipCode}
                onChange={(e) => setProfileForm({ ...profileForm, zipCode: e.target.value.replace(/\D/g, '').slice(0, 5) })}
                maxLength={5}
              />
            </div>

            <div>
              <Label htmlFor="emailForInvoices">Email para Facturas *</Label>
              <Input
                id="emailForInvoices"
                type="email"
                placeholder="email@ejemplo.com"
                value={profileForm.emailForInvoices}
                onChange={(e) => setProfileForm({ ...profileForm, emailForInvoices: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="defaultUsoCfdi">Uso CFDI Predeterminado</Label>
              <Select
                value={profileForm.defaultUsoCfdi}
                onValueChange={(value) => setProfileForm({ ...profileForm, defaultUsoCfdi: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione uso CFDI" />
                </SelectTrigger>
                <SelectContent>
                  {usosCfdi.map(uso => (
                    <SelectItem key={uso.code} value={uso.code}>
                      {uso.code} - {uso.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="isDefault"
                checked={profileForm.isDefault}
                onCheckedChange={(checked) => setProfileForm({ ...profileForm, isDefault: !!checked })}
              />
              <Label htmlFor="isDefault" className="font-normal">Usar como perfil predeterminado</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProfileDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveProfile} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingProfile ? 'Actualizar' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Solicitar Factura</DialogTitle>
            <DialogDescription>
              Confirma los datos para generar tu factura
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">Pagos seleccionados: {selectedPayments.length}</p>
              <p className="text-lg font-semibold">Total: {formatCurrency(selectedTotal)}</p>
            </div>

            <div>
              <Label htmlFor="taxProfileId">¿A qué RFC deseas facturar? *</Label>
              <Select
                value={invoiceForm.taxProfileId}
                onValueChange={(value) => {
                  const profile = taxProfiles.find(p => p.id.toString() === value);
                  setInvoiceForm({
                    ...invoiceForm,
                    taxProfileId: value,
                    usoCfdi: profile?.defaultUsoCfdi || 'D10',
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione perfil fiscal" />
                </SelectTrigger>
                <SelectContent>
                  {taxProfiles.map(profile => (
                    <SelectItem key={profile.id} value={profile.id.toString()}>
                      {profile.profileName} - {profile.rfc}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="usoCfdi">Uso CFDI *</Label>
              <Select
                value={invoiceForm.usoCfdi}
                onValueChange={(value) => setInvoiceForm({ ...invoiceForm, usoCfdi: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione uso CFDI" />
                </SelectTrigger>
                <SelectContent>
                  {usosCfdi.map(uso => (
                    <SelectItem key={uso.code} value={uso.code}>
                      {uso.code} - {uso.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>Cancelar</Button>
            <Button onClick={handleGenerateInvoice} disabled={generatingInvoice}>
              {generatingInvoice && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Generar Factura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
